import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CurrencyDisplay, { CurrencyLabel } from '../components/CurrencyDisplay';
import BarberStatementSheet from '../components/BarberStatementSheet';
import SalonSixMonthSheet from '../components/SalonSixMonthSheet';
import useElementToPdf from '../utils/useElementToPdf';
import { 
  Wallet, 
  TrendingUp, 
  Users, 
  Calendar,
  CheckCircle,
  Clock,
  Calculator,
  Percent,
  Plus,
  Minus,
  FileText,
  Building2,
  AlertTriangle,
  Printer,
  Receipt
} from 'lucide-react';

const MINIMUM_SALARY = 1800;

const Salaries = () => {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const { language } = useLanguage();
  const [salaries, setSalaries] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showCalculator, setShowCalculator] = useState(false);
  const [showMonthlyReport, setShowMonthlyReport] = useState(false);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [calculatorData, setCalculatorData] = useState({
    barber_id: '',
    month: new Date().toISOString().slice(0, 7),
    percentage: 40,
    bonus: 0,
    deductions: 0,
    notes: '',
    absorb_deficit: false
  });
  // Trial Period Management (none here — placeholder removed)
  const [monthlyRevenue, setMonthlyRevenue] = useState(null);

  // ===== Barber Statement (كشف حساب) =====
  const [showStatement, setShowStatement] = useState(false);
  const [statementBarberId, setStatementBarberId] = useState('');
  const [statementMonth, setStatementMonth] = useState(new Date().toISOString().slice(0, 7));
  const [statementData, setStatementData] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [pctOverride, setPctOverride] = useState(''); // admin live %
  const [salonReport, setSalonReport] = useState(null);
  const [salonReportLoading, setSalonReportLoading] = useState(false);
  const [salonReportMonths, setSalonReportMonths] = useState(6); // admin chooses 2..6
  const salonReportRef = useRef(null);
  const handlePrintSalonReport = useElementToPdf({
    contentRef: salonReportRef,
    documentTitle: `KE_Saloon_6Months_${new Date().toISOString().slice(0,7)}`,
    pageStyle: '@page { size: A4 landscape; margin: 8mm; } @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }',
  });
  const statementRef = useRef(null);
  const handlePrintStatement = useElementToPdf({
    contentRef: statementRef,
    documentTitle: statementData
      ? `KE_Saloon_Statement_${statementData.barber_name}_${statementData.month}`
      : 'KE_Saloon_Statement',
    pageStyle: '@page { size: A4 portrait; margin: 0; } @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; margin: 0; padding: 0; } html, body { height: auto !important; } * { box-sizing: border-box; } }',
  });

  // Print is locked until the 1st day of the month AFTER the statement month.
  // Example: a statement for 2026-05 is printable starting 2026-06-01 (UAE time).
  const isStatementPrintable = (ym) => {
    if (!ym) return false;
    try {
      const [y, m] = ym.split('-').map((x) => parseInt(x, 10));
      if (!y || !m) return false;
      // First instant of the month AFTER the statement month (UAE local time).
      // Using UTC+4 fixed offset (UAE has no DST) keeps the boundary stable.
      const nextMonthStartUtcMs = Date.UTC(y, m, 1, 0, 0, 0) - 4 * 3600 * 1000;
      return Date.now() >= nextMonthStartUtcMs;
    } catch {
      return false;
    }
  };

  const printLockMessage = (ym) => {
    if (!ym) return '';
    try {
      const [y, m] = ym.split('-').map((x) => parseInt(x, 10));
      const next = new Date(Date.UTC(y, m, 1, 0, 0, 0) - 4 * 3600 * 1000);
      const iso = new Intl.DateTimeFormat('en-CA', {
        day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Dubai',
      }).format(next);
      const [yy, mm, dd] = iso.split('-');
      return `الطباعة متاحة من ${dd}/${mm}/${yy} (أول يوم في الشهر التالي).`;
    } catch {
      return '';
    }
  };

  const fetchStatement = async (barberId, month, overridePct = null) => {
    if (!barberId || !month) {
      setStatementData(null);
      return;
    }
    setStatementLoading(true);
    try {
      const params = {};
      if (overridePct !== null && overridePct !== '' && !Number.isNaN(Number(overridePct))) {
        params.percentage_override = Number(overridePct);
      }
      const res = await api.get(`/salaries/barber-statement/${barberId}?month=${month}`, { params });
      setStatementData(res.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'فشل تحميل كشف الحساب');
      setStatementData(null);
    } finally {
      setStatementLoading(false);
    }
  };

  const openStatement = async () => {
    setShowStatement(true);
    setPctOverride(''); // reset override on fresh open
    // Barber: use their own barber_id; Admin: keep selector
    if (!isAdmin()) {
      const myBarberId = user?.barber_id;
      if (!myBarberId) {
        toast.error('لم نتمكن من تحديد حساب الحلاق');
        return;
      }
      setStatementBarberId(myBarberId);
      await fetchStatement(myBarberId, statementMonth);
    } else if (statementBarberId && statementMonth) {
      await fetchStatement(statementBarberId, statementMonth);
    }
  };

  useEffect(() => {
    if (showStatement && statementBarberId && statementMonth) {
      fetchStatement(statementBarberId, statementMonth, pctOverride);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementBarberId, statementMonth, pctOverride]);

  // Salon-wide 6-month report (admin only)
  const loadSalonReport = async () => {
    setSalonReportLoading(true);
    try {
      const res = await api.get(`/salaries/salon-six-month-report?months=${salonReportMonths}`);
      setSalonReport(res.data);
      setTimeout(() => handlePrintSalonReport(), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تحميل تقرير الصالون');
    } finally {
      setSalonReportLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [salariesRes, barbersRes] = await Promise.all([
        api.get('/salaries/my-salaries'),
        isAdmin() ? api.get('/barbers') : Promise.resolve({ data: [] })
      ]);
      setSalaries(salariesRes.data);
      setBarbers(barbersRes.data || []);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyRevenue = async (barberId, month) => {
    if (!barberId || !month) return;
    try {
      const res = await api.get(`/salaries/barber-monthly-revenue/${barberId}?month=${month}`);
      setMonthlyRevenue(res.data);
    } catch (error) {
      console.error('Failed to fetch revenue');
    }
  };

  const fetchMonthlyReport = async () => {
    try {
      setReportLoading(true);
      const res = await api.get(`/salaries/calculate-monthly-report?month=${selectedMonth}`);
      setMonthlyReport(res.data);
      setShowMonthlyReport(true);
    } catch (error) {
      toast.error('فشل تحميل التقرير الشهري');
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (calculatorData.barber_id && calculatorData.month) {
      fetchMonthlyRevenue(calculatorData.barber_id, calculatorData.month);
    }
  }, [calculatorData.barber_id, calculatorData.month]);

  const handleCalculateSalary = async () => {
    if (!calculatorData.barber_id) {
      toast.error('يرجى اختيار الحلاق');
      return;
    }

    try {
      const res = await api.post('/salaries/calculate', calculatorData);
      toast.success(res.data.message);
      setShowCalculator(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حساب الراتب');
    }
  };

  const handleMarkPaid = async (salaryId) => {
    try {
      await api.put(`/salaries/${salaryId}/pay`);
      toast.success('تم تحديث حالة الراتب');
      fetchData();
    } catch (error) {
      toast.error('فشل تحديث الراتب');
    }
  };

  const calculatePreview = () => {
    if (!monthlyRevenue) return null;
    const revenue = monthlyRevenue.total_revenue || 0;
    const commitmentShare = monthlyRevenue.commitment_share || 0;
    // معادلة جديدة: الراتب = (الإيراد − حصة الالتزامات) × النسبة، ثم حد أدنى 1800
    const netRevenue = Math.max(revenue - commitmentShare, 0);
    const base = netRevenue * (calculatorData.percentage / 100);
    let finalSalary = base + calculatorData.bonus - calculatorData.deductions;
    let deficit = 0;

    if (finalSalary < MINIMUM_SALARY) {
      deficit = MINIMUM_SALARY - finalSalary;
      finalSalary = MINIMUM_SALARY;
    }

    return { revenue, commitmentShare, netRevenue, base, final: finalSalary, deficit };
  };

  const preview = calculatePreview();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t('salaries')}</h1>
          <p className="text-muted-foreground">
            {isAdmin() ? 'إدارة رواتب الحلاقين' : 'رواتبي'}
          </p>
        </div>
        {isAdmin() && (
          <div className="flex gap-3">
            <div className="flex items-center gap-2">
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-secondary/50 w-40"
              />
              <Button 
                onClick={fetchMonthlyReport}
                disabled={reportLoading}
                variant="outline"
                className="gap-2"
              >
                <FileText className="w-4 h-4" />
                {reportLoading ? 'جاري التحميل...' : 'التقرير الشهري'}
              </Button>
            </div>
            <Button 
              onClick={() => setShowCalculator(true)}
              className="bg-primary text-primary-foreground"
            >
              <Calculator className="w-4 h-4 me-2" />
              حساب راتب جديد
            </Button>
          </div>
        )}
        {!isAdmin() && (
          <Button
            onClick={openStatement}
            variant="outline"
            className="gap-2"
            data-testid="open-my-statement-btn"
          >
            <Receipt className="w-4 h-4" />
            كشف حسابي
          </Button>
        )}
      </div>

      {isAdmin() && (
        <div className="flex justify-end gap-2 flex-wrap items-end">
          <div className="flex items-center gap-2 bg-purple-500/5 border border-purple-500/30 rounded-lg p-2">
            <label className="text-xs text-purple-300 whitespace-nowrap">عدد الأشهر</label>
            <select
              value={salonReportMonths}
              onChange={(e) => setSalonReportMonths(Number(e.target.value))}
              className="bg-secondary/50 border border-white/10 rounded-md p-1 text-sm"
              data-testid="salon-6m-months-select"
            >
              {[2,3,4,5,6].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <Button
              onClick={loadSalonReport}
              disabled={salonReportLoading}
              variant="outline"
              className="gap-2 border-purple-500/40 text-purple-400 hover:bg-purple-500/10"
              data-testid="salon-6m-report-btn"
              size="sm"
            >
              <FileText className="w-4 h-4" />
              {salonReportLoading ? 'جارٍ التوليد...' : `تقرير ${salonReportMonths} أشهر للصالون (PDF)`}
            </Button>
          </div>
          <Button
            onClick={openStatement}
            variant="outline"
            className="gap-2 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
            data-testid="open-statement-btn"
          >
            <Receipt className="w-4 h-4" />
            كشف حساب حلاق
          </Button>
        </div>
      )}

      {/* Statement Modal */}
      {showStatement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="statement-modal">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Receipt className="w-5 h-5 text-amber-400" />
                كشف حساب الحلاق
              </h2>
              <button
                onClick={() => setShowStatement(false)}
                className="text-muted-foreground hover:text-white text-xl"
                data-testid="close-statement-btn"
              >
                ×
              </button>
            </div>

            {/* Selector row (admin only) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {isAdmin() && (
                <div>
                  <label className="block text-sm font-medium mb-2">اختر الحلاق</label>
                  <select
                    value={statementBarberId}
                    onChange={(e) => setStatementBarberId(e.target.value)}
                    className="w-full bg-secondary/50 border border-white/10 rounded-md p-2"
                    data-testid="statement-barber-select"
                  >
                    <option value="">اختر الحلاق...</option>
                    {barbers.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}{b.is_excluded ? ' (مستثنى)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-2">الشهر</label>
                <Input
                  type="month"
                  value={statementMonth}
                  onChange={(e) => setStatementMonth(e.target.value)}
                  className="bg-secondary/50"
                  data-testid="statement-month-input"
                />
              </div>
              {isAdmin() && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    تعديل النسبة % <span className="text-xs text-muted-foreground">(محاكاة فقط)</span>
                  </label>
                  <Input
                    type="number"
                    min={10}
                    max={70}
                    step={1}
                    placeholder={statementData?.salary?.saved_percentage ? `الحالية ${statementData.salary.saved_percentage}%` : '20-40'}
                    value={pctOverride}
                    onChange={(e) => setPctOverride(e.target.value)}
                    className="bg-secondary/50"
                    data-testid="statement-pct-override"
                  />
                </div>
              )}
            </div>

            {statementLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}

            {!statementLoading && !statementData && (
              <div className="text-center text-muted-foreground py-6">
                اختر حلاقاً والشهر لعرض كشف الحساب
              </div>
            )}

            {!statementLoading && statementData && (
              <>
                {/* Admin-only full breakdown (extra detail). Barber only sees the print summary below. */}
                {isAdmin() && (
                  <div className="bg-secondary/30 border border-white/10 rounded-lg p-4 mb-4 text-sm space-y-2" data-testid="statement-admin-details">
                    {statementData.salary?.is_simulation && (
                      <div className="bg-purple-500/15 border border-purple-500/40 rounded-md p-2 text-purple-300 text-xs flex items-center justify-between gap-3" data-testid="statement-simulation-badge">
                        <span>🧪 محاكاة فقط (نسبة: {pctOverride}%) — الراتب المحفوظ يستخدم {statementData.salary.saved_percentage}%</span>
                        <Button
                          size="sm"
                          className="bg-purple-500 hover:bg-purple-600 text-white text-xs"
                          onClick={async () => {
                            try {
                              await api.post('/salaries/calculate', {
                                barber_id: statementData.barber_id,
                                month: statementData.month,
                                percentage: Number(pctOverride),
                                bonus: statementData.salary?.bonus || 0,
                                deductions: statementData.salary?.deductions || 0,
                                absorb_deficit: !!statementData.salary?.absorb_deficit,
                              });
                              toast.success('تم حفظ النسبة الجديدة');
                              setPctOverride('');
                              fetchStatement(statementBarberId, statementMonth);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'فشل الحفظ');
                            }
                          }}
                          data-testid="statement-save-pct-btn"
                        >
                          حفظ النسبة
                        </Button>
                      </div>
                    )}
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-muted-foreground">إجمالي إيرادات الشهر</span>
                      <CurrencyDisplay amount={statementData.revenue?.total} />
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-muted-foreground">عدد العملاء</span>
                      <span>{statementData.revenue?.customers || 0}</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-muted-foreground">حصة الالتزامات</span>
                      <span className="text-blue-400">−<CurrencyDisplay amount={statementData.commitments?.share} className="text-blue-400" /></span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-muted-foreground">الراتب المحسوب ({statementData.salary?.percentage}%)</span>
                      <CurrencyDisplay amount={statementData.salary?.calculated_salary} />
                    </div>
                    {Number(statementData.salary?.salary_deficit) > 0 && (
                      <div className="flex justify-between border-b border-white/5 pb-2">
                        <span className={statementData.salary?.absorb_deficit ? 'text-orange-400' : 'text-yellow-500'}>
                          {statementData.salary?.absorb_deficit ? 'فرق الحد الأدنى (تحمَّله الصالون)' : 'فرق الحد الأدنى (يُسجَّل كعجز)'}
                        </span>
                        <span className={statementData.salary?.absorb_deficit ? 'text-orange-400' : 'text-yellow-500'}>
                          +<CurrencyDisplay amount={statementData.salary.salary_deficit} className={statementData.salary?.absorb_deficit ? 'text-orange-400' : 'text-yellow-500'} />
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span>الراتب النهائي</span>
                      <span className="font-bold text-primary"><CurrencyDisplay amount={statementData.salary?.final_salary} className="text-primary" /></span>
                    </div>
                    {Number(statementData.previous_deficit?.total) > 0 && (
                      <div className="flex justify-between border-b border-white/5 pb-2">
                        <span className="text-red-400">− عجز متراكم ({statementData.previous_deficit.count} شهر)</span>
                        <span className="text-red-400">−<CurrencyDisplay amount={statementData.previous_deficit.total} className="text-red-400" /></span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2">
                      <span className="text-base font-bold">الرصيد المستحق</span>
                      <span className="text-base font-bold text-green-400" data-testid="statement-net-payable">
                        <CurrencyDisplay amount={statementData.net_payable} className="text-green-400" />
                      </span>
                    </div>
                  </div>
                )}

                {/* Barber-friendly summary (always visible) */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
                  <div className="text-sm text-muted-foreground mb-1">الرصيد المستحق لشهر {statementData.month}</div>
                  <div className="text-3xl font-bold text-amber-400 text-center py-2" data-testid="statement-payable-amount">
                    <CurrencyDisplay amount={statementData.net_payable} className="text-amber-400" />
                  </div>
                  <div className="text-xs text-muted-foreground text-center">
                    تاريخ الإصدار: {new Date(statementData.print_date).toLocaleString('ar-AE', { timeZone: 'Asia/Dubai', hour12: false })}
                  </div>
                </div>

                {/* Payment history — last 6 months */}
                {statementData.payment_history?.length > 0 && (
                  <div className="bg-secondary/30 border border-white/10 rounded-lg p-4 mb-4" data-testid="statement-payment-history">
                    <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-400" />
                      سجل آخر 6 أشهر
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-muted-foreground">
                          <tr className="border-b border-white/5">
                            <th className="text-start p-2">الشهر</th>
                            <th className="text-start p-2">الراتب</th>
                            <th className="text-start p-2">الحالة</th>
                            <th className="text-start p-2">تاريخ الصرف</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statementData.payment_history.map((h) => (
                            <tr key={h.month} className="border-b border-white/5">
                              <td className="p-2 font-medium">{h.month}</td>
                              <td className="p-2"><CurrencyDisplay amount={h.final_salary} /></td>
                              <td className="p-2">
                                {h.status === 'paid' ? (
                                  <span className="inline-flex items-center gap-1 text-green-400">
                                    <CheckCircle className="w-3 h-3" /> مدفوع
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-yellow-400">
                                    <Clock className="w-3 h-3" /> معلّق
                                  </span>
                                )}
                              </td>
                              <td className="p-2 text-muted-foreground">
                                {h.paid_date
                                  ? new Date(h.paid_date).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }).split('-').reverse().join('/')
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Lock notice */}
                {!isStatementPrintable(statementData.month) && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-3 text-sm text-blue-300" data-testid="statement-print-lock-notice">
                    🔒 {printLockMessage(statementData.month)}
                  </div>
                )}
                {isStatementPrintable(statementData.month) && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 mb-3 text-xs text-amber-300" data-testid="statement-without-support-notice">
                    ملاحظة: ملف PDF يُظهر الراتب الفعلي للحلاق (بدون الرفع للحد الأدنى).
                  </div>
                )}

                <Button
                  onClick={handlePrintStatement}
                  disabled={!isStatementPrintable(statementData.month)}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black gap-2 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="print-statement-btn"
                >
                  <Printer className="w-4 h-4" />
                  طباعة كشف الحساب (PDF)
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Hidden printable statement sheet — rendered off-screen so the PDF generator can capture it */}
      {statementData && (
        <div style={{ position: 'fixed', left: '-10000px', top: 0, pointerEvents: 'none' }} aria-hidden="true">
          <BarberStatementSheet ref={statementRef} statement={statementData} />
        </div>
      )}

      {/* Hidden 6-month salon sheet */}
      {salonReport && (
        <div style={{ position: 'fixed', left: '-10000px', top: 0, pointerEvents: 'none' }} aria-hidden="true">
          <SalonSixMonthSheet ref={salonReportRef} data={salonReport} />
        </div>
      )}

      {/* Monthly Report Modal */}
      {showMonthlyReport && monthlyReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              التقرير الشهري - {monthlyReport.month}
            </h2>

            {/* Commitments Summary */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-blue-400 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                الالتزامات الثابتة
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">الإيجار الشهري:</span>
                  <p className="font-bold"><CurrencyDisplay amount={monthlyReport.commitments?.monthly_rent || 0} /></p>
                </div>
                <div>
                  <span className="text-muted-foreground">حصة الرخصة:</span>
                  <p className="font-bold"><CurrencyDisplay amount={monthlyReport.commitments?.monthly_license || 0} /></p>
                </div>
                <div>
                  <span className="text-muted-foreground">الفواتير:</span>
                  <p className="font-bold"><CurrencyDisplay amount={monthlyReport.commitments?.monthly_bills_total || 0} /></p>
                </div>
                <div>
                  <span className="text-muted-foreground">الإجمالي:</span>
                  <p className="font-bold text-blue-400"><CurrencyDisplay amount={monthlyReport.commitments?.total_monthly || 0} className="text-blue-400" /></p>
                </div>
                <div>
                  <span className="text-muted-foreground">حصة كل حلاق:</span>
                  <p className="font-bold text-primary"><CurrencyDisplay amount={monthlyReport.commitments?.per_barber || 0} className="text-primary" /></p>
                </div>
              </div>
            </div>

            {/* Barbers Report */}
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead className="bg-secondary/30">
                  <tr>
                    <th className="p-3 text-start">الحلاق</th>
                    <th className="p-3 text-start">الإيراد</th>
                    <th className="p-3 text-start">حصة الالتزامات</th>
                    <th className="p-3 text-start">الراتب المحسوب</th>
                    <th className="p-3 text-start">الراتب النهائي</th>
                    <th className="p-3 text-start">العجز</th>
                    <th className="p-3 text-start">ربح الصالون</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyReport.barbers?.map((barber) => (
                    <tr key={barber.barber_id} className="border-t border-white/5">
                      <td className="p-3 font-medium">{barber.barber_name}</td>
                      <td className="p-3"><CurrencyDisplay amount={barber.total_revenue} /></td>
                      <td className="p-3 text-blue-400">-<CurrencyDisplay amount={barber.commitment_share} className="text-blue-400" /></td>
                      <td className="p-3">
                        <CurrencyDisplay amount={barber.calculated_salary} />
                        <span className="text-muted-foreground text-xs ms-1">({barber.salary_percentage}%)</span>
                      </td>
                      <td className="p-3 font-bold text-primary"><CurrencyDisplay amount={barber.final_salary} className="text-primary" /></td>
                      <td className="p-3">
                        {barber.salary_deficit > 0 ? (
                          barber.absorb_deficit ? (
                            <span className="text-orange-400 flex items-center gap-1" title="يتحمّله الصالون">
                              <AlertTriangle className="w-3 h-3" />
                              <CurrencyDisplay amount={barber.salary_deficit} className="text-orange-400" />
                              <span className="text-[10px]">صالون</span>
                            </span>
                          ) : (
                            <span className="text-red-500 flex items-center gap-1" title="عجز على الحلاق">
                              <AlertTriangle className="w-3 h-3" />
                              -<CurrencyDisplay amount={barber.salary_deficit} className="text-red-500" />
                            </span>
                          )
                        ) : (
                          <span className="text-green-500">0</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className={barber.salon_profit >= 0 ? 'text-green-500' : 'text-red-500'}>
                          <CurrencyDisplay amount={barber.salon_profit} className={barber.salon_profit >= 0 ? 'text-green-500' : 'text-red-500'} />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-secondary/20 rounded-lg p-4 text-center">
                <p className="text-muted-foreground text-sm">إجمالي الإيرادات</p>
                <p className="text-xl font-bold"><CurrencyDisplay amount={monthlyReport.summary?.total_revenue || 0} /></p>
              </div>
              <div className="bg-secondary/20 rounded-lg p-4 text-center">
                <p className="text-muted-foreground text-sm">إجمالي الرواتب</p>
                <p className="text-xl font-bold text-primary"><CurrencyDisplay amount={monthlyReport.summary?.total_salaries || 0} className="text-primary" /></p>
              </div>
              <div className="bg-red-500/10 rounded-lg p-4 text-center">
                <p className="text-muted-foreground text-sm">إجمالي العجز</p>
                <p className="text-xl font-bold text-red-500"><CurrencyDisplay amount={monthlyReport.summary?.total_deficit || 0} className="text-red-500" /></p>
              </div>
              <div className="bg-green-500/10 rounded-lg p-4 text-center">
                <p className="text-muted-foreground text-sm">صافي ربح الصالون</p>
                <p className={`text-xl font-bold ${(monthlyReport.summary?.total_salon_profit || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  <CurrencyDisplay amount={monthlyReport.summary?.total_salon_profit || 0} className={(monthlyReport.summary?.total_salon_profit || 0) >= 0 ? 'text-green-500' : 'text-red-500'} />
                </p>
              </div>
            </div>

            {/* Info */}
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-4 text-sm">
              <p className="text-yellow-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                الحد الأدنى للراتب: <CurrencyDisplay amount={MINIMUM_SALARY} className="text-yellow-400" showIcon={false} /> - إذا كان الراتب أقل يُرفع تلقائياً.
                إذا كانت خانة "تحمّل الصالون لفرق الحد الأدنى" مفعّلة عند إنشاء الراتب يتحمّله الصالون، وإلا يُسجَّل كعجز على الحلاق.
              </p>
              {monthlyReport.commitments?.eligible_barbers_count && (
                <p className="text-blue-400 flex items-center gap-2 mt-2">
                  <Building2 className="w-4 h-4" />
                  حصة الالتزامات الشهرية تُوزَّع على {monthlyReport.commitments.eligible_barbers_count} حلاق نشط غير مستثنى، وتُخصم من الإيراد قبل احتساب نسبة الراتب.
                </p>
              )}
            </div>

            <Button onClick={() => setShowMonthlyReport(false)} variant="outline" className="w-full">
              إغلاق
            </Button>
          </div>
        </div>
      )}

      {/* Salary Calculator Modal */}
      {showCalculator && isAdmin() && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-primary" />
              حساب الراتب
            </h2>

            <div className="space-y-4">
              {/* Barber Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">الحلاق</label>
                <select
                  value={calculatorData.barber_id}
                  onChange={(e) => setCalculatorData({ ...calculatorData, barber_id: e.target.value })}
                  className="w-full bg-secondary/50 border border-white/10 rounded-md p-2"
                >
                  <option value="">اختر الحلاق...</option>
                  {barbers.map((barber) => (
                    <option key={barber.id} value={barber.id}>{barber.name}</option>
                  ))}
                </select>
              </div>

              {/* Month Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">الشهر</label>
                <Input
                  type="month"
                  value={calculatorData.month}
                  onChange={(e) => setCalculatorData({ ...calculatorData, month: e.target.value })}
                  className="bg-secondary/50"
                />
              </div>

              {/* Monthly Revenue Display */}
              {monthlyRevenue && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <h3 className="font-semibold text-primary mb-2">إيرادات الشهر</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">إجمالي الإيراد:</span>
                      <p className="text-xl font-bold"><CurrencyDisplay amount={monthlyRevenue.total_revenue} /></p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">عدد العملاء:</span>
                      <p className="text-xl font-bold">{monthlyRevenue.total_customers}</p>
                    </div>
                    {monthlyRevenue.commitment_share != null && (
                      <div className="col-span-2 pt-2 border-t border-white/10">
                        <span className="text-muted-foreground text-xs">
                          حصة الالتزامات الشهرية (تُخصم قبل احتساب النسبة):
                        </span>
                        <p className="font-bold text-blue-400">
                          −<CurrencyDisplay amount={monthlyRevenue.commitment_share} className="text-blue-400" />
                          <span className="text-xs text-muted-foreground ms-2">
                            ({monthlyRevenue.eligible_barbers_count || 1} حلاق نشط غير مستثنى)
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Percentage */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  النسبة (20% - 40%)
                </label>
                <div className="flex items-center gap-4">
                  <Input
                    type="range"
                    min="20"
                    max="40"
                    value={calculatorData.percentage}
                    onChange={(e) => setCalculatorData({ ...calculatorData, percentage: parseInt(e.target.value) })}
                    className="flex-1"
                  />
                  <span className="text-2xl font-bold text-primary w-16 text-center">
                    {calculatorData.percentage}%
                  </span>
                </div>
              </div>

              {/* Bonus */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Plus className="w-4 h-4 inline me-1 text-green-500" />
                  مكافأة إضافية
                </label>
                <Input
                  type="number"
                  value={calculatorData.bonus}
                  onChange={(e) => setCalculatorData({ ...calculatorData, bonus: parseFloat(e.target.value) || 0 })}
                  className="bg-secondary/50"
                  min="0"
                />
              </div>

              {/* Deductions */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Minus className="w-4 h-4 inline me-1 text-red-500" />
                  خصومات
                </label>
                <Input
                  type="number"
                  value={calculatorData.deductions}
                  onChange={(e) => setCalculatorData({ ...calculatorData, deductions: parseFloat(e.target.value) || 0 })}
                  className="bg-secondary/50"
                  min="0"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-2">ملاحظات</label>
                <Input
                  value={calculatorData.notes}
                  onChange={(e) => setCalculatorData({ ...calculatorData, notes: e.target.value })}
                  className="bg-secondary/50"
                  placeholder="ملاحظات إضافية..."
                />
              </div>

              {/* Absorb deficit checkbox */}
              <div className="flex items-start gap-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <input
                  type="checkbox"
                  id="absorb-deficit-checkbox"
                  data-testid="absorb-deficit-checkbox"
                  checked={calculatorData.absorb_deficit}
                  onChange={(e) => setCalculatorData({ ...calculatorData, absorb_deficit: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-yellow-500"
                />
                <label htmlFor="absorb-deficit-checkbox" className="text-sm flex-1 cursor-pointer">
                  <span className="font-medium text-yellow-400">
                    تحمّل الصالون لفرق الحد الأدنى (دعم الراتب حتى 1800)
                  </span>
                  <p className="text-muted-foreground text-xs mt-1">
                    عند تفعيله: لن يُسجَّل فرق الحد الأدنى كعجز على الحلاق، بل يتحمّله الصالون كنقص في الربح.
                  </p>
                </label>
              </div>

              {/* Preview */}
              {preview && monthlyRevenue && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <h3 className="font-semibold text-green-500 mb-2">معاينة الراتب</h3>
                  <div className="space-y-2 text-sm">
                    {preview.commitmentShare > 0 && (
                      <>
                        <div className="flex justify-between text-blue-400">
                          <span>الإيراد − حصة الالتزامات:</span>
                          <span>
                            {preview.revenue.toFixed(2)} − {preview.commitmentShare.toFixed(2)} =
                            <strong className="ms-1"><CurrencyDisplay amount={preview.netRevenue} className="text-blue-400" /></strong>
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>الصافي × النسبة:</span>
                          <span>{preview.netRevenue.toFixed(2)} × {calculatorData.percentage}% = <strong><CurrencyDisplay amount={preview.base} /></strong></span>
                        </div>
                      </>
                    )}
                    {preview.commitmentShare === 0 && (
                      <div className="flex justify-between">
                        <span>الإيراد × النسبة:</span>
                        <span>{preview.revenue.toFixed(2)} × {calculatorData.percentage}% = <strong><CurrencyDisplay amount={preview.base} /></strong></span>
                      </div>
                    )}
                    {calculatorData.bonus > 0 && (
                      <div className="flex justify-between text-green-500">
                        <span>+ مكافأة:</span>
                        <span><CurrencyDisplay amount={calculatorData.bonus} className="text-green-500" /></span>
                      </div>
                    )}
                    {calculatorData.deductions > 0 && (
                      <div className="flex justify-between text-red-500">
                        <span>- خصومات:</span>
                        <span><CurrencyDisplay amount={calculatorData.deductions} className="text-red-500" /></span>
                      </div>
                    )}
                    {preview.deficit > 0 && (
                      <div className={`flex justify-between p-2 rounded ${calculatorData.absorb_deficit ? 'text-orange-400 bg-orange-500/10' : 'text-yellow-500 bg-yellow-500/10'}`}>
                        <span className="flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          تعديل للحد الأدنى (<CurrencyDisplay amount={MINIMUM_SALARY} className={calculatorData.absorb_deficit ? 'text-orange-400' : 'text-yellow-500'} showIcon={false} />):
                        </span>
                        <span>
                          +<CurrencyDisplay amount={preview.deficit} className={calculatorData.absorb_deficit ? 'text-orange-400' : 'text-yellow-500'} />
                          <span className="text-xs ms-1">
                            ({calculatorData.absorb_deficit ? 'يتحمله الصالون' : 'يُسجل كعجز على الحلاق'})
                          </span>
                        </span>
                      </div>
                    )}
                    <hr className="border-white/10" />
                    <div className="flex justify-between text-lg font-bold">
                      <span>الراتب النهائي:</span>
                      <span className="text-primary"><CurrencyDisplay amount={preview.final} className="text-primary" /></span>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <Button
                  onClick={handleCalculateSalary}
                  className="flex-1 bg-primary text-primary-foreground"
                  disabled={!calculatorData.barber_id || !monthlyRevenue}
                >
                  حفظ الراتب
                </Button>
                <Button
                  onClick={() => setShowCalculator(false)}
                  variant="outline"
                  className="flex-1"
                >
                  إلغاء
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/20 rounded-lg">
              <Wallet className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">إجمالي الرواتب</p>
              <p className="text-2xl font-bold">
                <CurrencyDisplay amount={salaries.reduce((sum, s) => sum + (s.final_amount || s.amount || 0), 0)} />
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">مدفوع</p>
              <p className="text-2xl font-bold">
                {salaries.filter(s => s.status === 'paid').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">معلق</p>
              <p className="text-2xl font-bold">
                {salaries.filter(s => s.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Salaries Table */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <h3 className="font-semibold">سجل الرواتب</h3>
        </div>
        
        {salaries.length === 0 ? (
          <div className="p-12 text-center">
            <Wallet className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">لا توجد رواتب مسجلة</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/30">
                <tr>
                  {isAdmin() && <th className="p-4 text-start">الحلاق</th>}
                  <th className="p-4 text-start">الشهر</th>
                  <th className="p-4 text-start">الإيراد</th>
                  <th className="p-4 text-start">النسبة</th>
                  <th className="p-4 text-start">الراتب الأساسي</th>
                  <th className="p-4 text-start">مكافأة</th>
                  <th className="p-4 text-start">خصومات</th>
                  <th className="p-4 text-start">الراتب النهائي</th>
                  <th className="p-4 text-start">الحالة</th>
                  {isAdmin() && <th className="p-4 text-start">إجراءات</th>}
                </tr>
              </thead>
              <tbody>
                {salaries.map((salary) => (
                  <tr key={salary.id} className="border-t border-white/5 hover:bg-secondary/20">
                    {isAdmin() && (
                      <td className="p-4 font-medium">{salary.barber_name || '-'}</td>
                    )}
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        {salary.month}
                      </div>
                    </td>
                    <td className="p-4"><CurrencyDisplay amount={salary.revenue || 0} /></td>
                    <td className="p-4">
                      <span className="bg-primary/20 text-primary px-2 py-1 rounded-full text-sm">
                        {salary.percentage || 0}%
                      </span>
                    </td>
                    <td className="p-4"><CurrencyDisplay amount={salary.amount || 0} /></td>
                    <td className="p-4 text-green-500">+{(salary.bonus || 0).toFixed(2)}</td>
                    <td className="p-4 text-red-500">-{(salary.deductions || 0).toFixed(2)}</td>
                    <td className="p-4 font-bold text-primary">
                      <CurrencyDisplay amount={salary.final_amount || salary.amount || 0} className="text-primary" />
                    </td>
                    <td className="p-4">
                      {salary.status === 'paid' ? (
                        <span className="bg-green-500/20 text-green-500 px-3 py-1 rounded-full text-sm">
                          مدفوع
                        </span>
                      ) : (
                        <span className="bg-yellow-500/20 text-yellow-500 px-3 py-1 rounded-full text-sm">
                          معلق
                        </span>
                      )}
                    </td>
                    {isAdmin() && (
                      <td className="p-4">
                        {salary.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handleMarkPaid(salary.id)}
                            className="bg-green-500/20 text-green-500 hover:bg-green-500/30"
                          >
                            <CheckCircle className="w-4 h-4 me-1" />
                            دفع
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Salaries;
